import { EDITEUR } from './editeur.ts';

/**
 * Les prix, tels que Stripe les facture, mis en mots pour l'interface.
 *
 * Les montants viennent de `GET /billing/plans`, qui les lit chez Stripe : rien
 * n'est recopié ici. Ce module ne fait que les CALCULER pour un nombre de
 * comptes et les DIRE — la page des offres et l'écran Abonnement disent donc
 * la même chose, et c'est la même chose que la facture.
 */

/** Un tarif Stripe (centimes). `paliers` est vide pour un prix unitaire. */
export interface Tarif {
  devise: string;
  unitaire: number | null;
  mode: 'graduated' | 'volume' | null;
  paliers: Array<{ jusqua: number | null; unitaire: number; forfait: number }>;
}

export type Periodicite = 'MONTH' | 'YEAR';

export const PAR: Record<Periodicite, string> = { MONTH: 'par mois', YEAR: 'par an' };

/**
 * Montant d'une période pour `quantite` comptes, comme Stripe le calcule.
 *
 * Même règle que le serveur (`modules/billing/tarif.ts`) : paliers gradués —
 * chaque tranche à son prix, forfait compris dès qu'elle est entamée — ou par
 * volume — toute la quantité au prix de la tranche atteinte.
 */
export function montantPour(tarif: Tarif, quantite: number): number | null {
  if (tarif.unitaire !== null) return tarif.unitaire * quantite;
  const paliers = tarif.paliers;
  if (paliers.length === 0) return null;
  if (tarif.mode === 'volume') {
    const p = paliers.find((t) => t.jusqua === null || quantite <= t.jusqua) ?? paliers.at(-1)!;
    return p.unitaire * quantite + p.forfait;
  }
  let total = 0;
  let comptees = 0;
  for (const t of paliers) {
    if (comptees >= quantite) break;
    const dans = Math.min(quantite, t.jusqua ?? Infinity) - comptees;
    if (dans <= 0) continue;
    total += dans * t.unitaire + t.forfait;
    comptees += dans;
  }
  return total;
}

/** 4000 → « 40 € » ; 867 → « 8,67 € ». Les centimes ne s'affichent que s'il y en a. */
export function euros(centimes: number, devise = 'eur'): string {
  return (centimes / 100).toLocaleString('fr-FR', {
    style: 'currency',
    currency: devise.toUpperCase(),
    minimumFractionDigits: centimes % 100 === 0 ? 0 : 2,
  });
}

const comptes = (n: number) => `${n} compte${n > 1 ? 's' : ''}`;

/**
 * La structure d'un tarif à paliers, en une phrase :
 * « 40 € jusqu'à 2 comptes, puis 10 € par compte supplémentaire ».
 */
export function decrirePaliers(tarif: Tarif): string | null {
  if (tarif.unitaire !== null || tarif.paliers.length === 0) return null;
  const d = tarif.devise;
  return tarif.paliers.map((t, i) => {
    const prix = t.forfait > 0 && t.unitaire === 0
      ? euros(t.forfait, d)
      : t.forfait > 0
        ? `${euros(t.forfait, d)} + ${euros(t.unitaire, d)} par compte`
        : `${euros(t.unitaire, d)} par compte`;
    if (i === 0) return t.jusqua === null ? prix : `${prix} jusqu’à ${comptes(t.jusqua)}`;
    const suite = t.jusqua === null
      ? (tarif.mode === 'volume' ? 'au-delà' : 'supplémentaire')
      : `jusqu’à ${comptes(t.jusqua)}`;
    return t.unitaire > 0 && t.forfait === 0 && suite === 'supplémentaire'
      ? `puis ${euros(t.unitaire, d)} par compte supplémentaire`
      : `puis ${prix} ${suite}`;
  }).join(', ');
}

/** La mention fiscale qui accompagne chaque prix affiché. */
export function mentionTva(): string {
  return EDITEUR.franchiseDeTva
    ? 'TVA non applicable, art. 293 B du CGI'
    : 'Prix TTC';
}
